import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../css/Home.css";
import axios from "axios";
import AdLeft from "./AdLeft";
import AdRight from "./AdRight";
import { handleSearch } from "../utils/search";
import { getDaysDiff } from "../utils/dateUtils";
import { checkSessionUser } from "../utils/auth";
import { handleNewsClickUtil } from "../utils/newsClick";
import { fetchRankingUtil } from "../utils/ranking";
import Footer from "./Footer";
import Header from "./Header";

axios.defaults.withCredentials = true;

function SearchHistory() {
  const navigate = useNavigate();

  // 로그인/세션
  const [userInfo, setUserInfo] = useState(null);
  const [id, setId] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(null); // null=확인중, true/false=결과

  // 검색기록(Seek)
  const [posts, setPosts] = useState([]);
  const [msg, setMsg] = useState("");
  const postsPerPage = 5;

  const [lastId, setLastId] = useState(null);
  const [hasNext, setHasNext] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(false);

  // 랭킹
  const [rankings, setRankings] = useState([]);
  const [period, setPeriod] = useState("daily");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showList, setShowList] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // 검색 입력
  const [urlInput, setUrlInput] = useState("");

  // ✅ 0) 토큰 세팅 + 세션 확인 (한 번만)
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (!token) {
      delete axios.defaults.headers.common["Authorization"];
      setIsLoggedIn(false);
      alert("비정상적인 접근이 감지되었습니다.");
      navigate("/");
      return;
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    checkSessionUser(setUserInfo, setId, setIsLoggedIn);

    const savedUsername = localStorage.getItem("username");
    if (savedUsername) setId(savedUsername);
  }, [navigate]);

  // ✅ 1) 세션 체크 끝났는데 false면 리다이렉트
  useEffect(() => {
    if (isLoggedIn === false) {
      alert("비정상적인 접근이 감지되었습니다.");
      navigate("/");
    }
  }, [isLoggedIn, navigate]);

  // ✅ 2) 검색기록 최초 1회 로드 (여기서 무한루프 방지)
  useEffect(() => {
    if (isLoggedIn !== true) return;

    setPosts([]);
    setLastId(null);
    setHasNext(true);
    setMsg("");

    setLoadingPosts(true);
    axios
      .get(`${process.env.REACT_APP_SPRING_API_URL}/api/posts/seek`, {
        params: { size: postsPerPage, lastId: null },
      })
      .then((res) => {
        const content = Array.isArray(res.data?.content)
          ? res.data.content
          : [];
        const nextHas = !!res.data?.hasNext;

        setPosts(content);
        setHasNext(nextHas);

        if (content.length > 0) {
          setLastId(content[content.length - 1].id);
        }
      })
      .catch((err) => {
        console.error("검색기록 첫 로드 실패:", err);
        setMsg(err.response?.data?.message ?? "불러오기 실패");
      })
      .finally(() => setLoadingPosts(false));
  }, [isLoggedIn, postsPerPage]);

  // ✅ 3) 더보기(Seek) - 버튼 눌렀을 때만
  const loadMorePosts = useCallback(() => {
    if (loadingPosts) return;
    if (!hasNext) return;

    setLoadingPosts(true);

    axios
      .get(`${process.env.REACT_APP_SPRING_API_URL}/api/posts/seek`, {
        params: {
          size: postsPerPage,
          lastId, // 커서
        },
      })
      .then((res) => {
        const content = Array.isArray(res.data?.content)
          ? res.data.content
          : [];
        const nextHas = !!res.data?.hasNext;

        setPosts((prev) => [...prev, ...content]);
        setHasNext(nextHas);

        if (content.length > 0) {
          setLastId(content[content.length - 1].id);
        }
      })
      .catch((err) => {
        console.error("검색기록 더보기 실패:", err);
        setMsg(err.response?.data?.message ?? "불러오기 실패");
      })
      .finally(() => setLoadingPosts(false));
  }, [hasNext, lastId, loadingPosts, postsPerPage]);

  // ✅ 4) 랭킹 데이터
  useEffect(() => {
    fetchRankingUtil(period, setRankings, setLoading, setError);
  }, [period]);

  // ✅ 랭킹 자동 순환
  useEffect(() => {
    if (!Array.isArray(rankings) || rankings.length === 0) return;
    const intervalId = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.min(rankings.length, 10));
    }, 5000);
    return () => clearInterval(intervalId);
  }, [rankings]);

  const isLogin = !!userInfo?.username;
  const usernameForNews = userInfo?.username ?? id ?? "anonymous";

  const handleNewsClick = (e, url, username) => {
    e.preventDefault();
    handleNewsClickUtil(url, username, navigate);
  };

  return (
    <div className="Home-container">
      <Header />

      <div className="AdLeft-container">
        <AdLeft />
      </div>
      <div className="AdRight-container">
        <AdRight />
      </div>

      <div className="Home-main">
        <div className="Home-img"></div>
        <header className="Home-header">
          <div className="Home-home-container">
            <div className="Home-title-container">
              <button
                className="Home-home-button"
                type="button"
                onClick={() => navigate("/")}
              >
                <img
                  className="Home-home-button-img"
                  src="/snake.png"
                  alt="타이틀 이미지"
                />
              </button>
            </div>
          </div>

          <p className="Home-body">
            정보를 원하는 기사의 URL을
            <br />
            입력해 보세요.
          </p>

          <div className="Home-search-container">
            <div className="Home-Search">
              <input
                id="Home-search-input"
                placeholder="뉴스 URL을 입력하세요."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch(urlInput, id, navigate);
                }}
              />
              <img
                className="Home-search-img"
                src="/icon.png"
                alt="돋보기"
                onClick={() => handleSearch(urlInput, id, navigate)}
              />
            </div>

            <div className="Home-period">
              <div className="ranking-period-select">
                <button
                  className={`daily ${period === "daily" ? "active" : ""}`}
                  onClick={() => setPeriod("daily")}
                >
                  일간
                </button>
                <button
                  className={`weekly ${period === "weekly" ? "active" : ""}`}
                  onClick={() => setPeriod("weekly")}
                >
                  주간
                </button>
                <button
                  className={`monthly ${period === "monthly" ? "active" : ""}`}
                  onClick={() => setPeriod("monthly")}
                >
                  월간
                </button>
              </div>

              <div className="Home-real-time-search">
                <div
                  className="top-company"
                  onClick={() => setShowList((p) => !p)}
                >
                  실시간 {currentIndex + 1}위:{" "}
                  {loading
                    ? "불러오는 중..."
                    : rankings[currentIndex]?.company || "데이터 없음"}
                </div>

                {showList && (
                  <ul className="ranking-list">
                    {error ? (
                      <li>{error}</li>
                    ) : (
                      rankings.slice(0, 10).map((item, index) => (
                        <li key={index}>
                          {index + 1}위:{" "}
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              if (item.company && item.company !== "—") {
                                navigate(
                                  `/${encodeURIComponent(item.company)}`,
                                  {
                                    state: { companyName: item.company },
                                  },
                                );
                              }
                            }}
                          >
                            {item.company}
                          </span>{" "}
                          ({item.companyCount}회)
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {!isLogin ? (
            <p className="Home-tail">
              로그인해서 전에 검색했던 내용을 다시 확인하세요!
            </p>
          ) : (
            <>
              {msg && <p className="Home-tail">{msg}</p>}

              {!msg && (
                <>
                  <p className="Home-tail-login">
                    더보기 버튼으로 검색 기록을 추가 로드합니다.
                  </p>

                  <div className="Home-news-list">
                    {posts.map((post) => (
                      <div key={post.id} className="Home-news-container">
                        <div className="Home-news-img-container">
                          <img
                            className="Home-news-img"
                            src={post.thumbnailUrl || "/default-thumbnail.png"}
                            alt="썸네일"
                          />
                        </div>

                        <div className="Home-news-detail-container">
                          <h2 className="Home-news-title">
                            <Link
                              to="/prediction"
                              state={{ url: post.url, id: usernameForNews }}
                              onClick={(e) =>
                                handleNewsClick(e, post.url, usernameForNews)
                              }
                            >
                              {post.title}
                            </Link>
                          </h2>

                          <p className="Home-news-summary">
                            <Link
                              to="/prediction"
                              state={{ url: post.url, id: usernameForNews }}
                              onClick={(e) =>
                                handleNewsClick(e, post.url, usernameForNews)
                              }
                            >
                              {post.content}
                              {post.time}
                            </Link>
                          </p>

                          <p className="Home-news-time">
                            <Link
                              to="/prediction"
                              state={{ url: post.url, id: usernameForNews }}
                              onClick={(e) =>
                                handleNewsClick(e, post.url, usernameForNews)
                              }
                            >
                              {post.newsTime
                                ? getDaysDiff(new Date(), post.newsTime) < 32
                                  ? `${getDaysDiff(new Date(), post.newsTime)}일 전`
                                  : post.newsTime
                                : ""}
                            </Link>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pagination">
                    <button
                      className="page-btn"
                      onClick={loadMorePosts}
                      disabled={loadingPosts || !hasNext}
                    >
                      {loadingPosts
                        ? "불러오는 중..."
                        : hasNext
                          ? "더보기"
                          : "끝!"}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </header>
      </div>

      <Footer />
    </div>
  );
}

export default SearchHistory;
